import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  COMMON_CONSTANTS,
  DateTimeFormatter,
  EventEmitter,
  getCurrentUserHook,
  getTranslatedText,
  toast,
  withErrorBoundary
} from '@nile/components';
import {
  GET_ALL_APPROVAL_STATUS,
  GET_APPROVALS,
  UPDATE_APPROVAL,
  useCachedQuery
} from '@nile/graphql';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { isEmpty } from 'lodash';
import { Tooltip } from 'react-clone-tippy';

const ApprovalCard = (props) => {
  const [comments, setComments] = useState('');
  const { approval, approvalStatuses } = props;
  const intl = useIntl();

  const [updateApproval, { loading: updateLoading }] = useMutation(
    UPDATE_APPROVAL,
    {
      onCompleted({ updateApproval }) {
        toast('success', 'Approval status updated successfully');
        EventEmitter.dispatch('approvalChange');
        EventEmitter.dispatch('refetchTicketApproval');
        setShowWidget(false);
      },
      onError() {}
    }
  );

  const approveTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'APPROVE'
    )?.name;
    updateApproval({
      variables: {
        approvalId: approval?.approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const rejectTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'REJECT'
    )?.name;
    updateApproval({
      variables: {
        approvalId: approval?.approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const askMoreInfo = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'NEED_MORE_INFORMATION'
    )?.name;
    updateApproval({
      variables: {
        approvalId: approval?.approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };

  return (
    <Card>
      <div className="p15">
        <Card noShadow variant="attention">
          <div className="p10">
            <div className="flex g10">
              <div>
                <Badge
                  status="attention"
                  icon={<SVGIcon name="Notes" />}
                  radial
                  size="large"
                />
              </div>
              <div>
                <TextStyle weight="SemiBold">
                  <FormattedMessage id="ticket.details.approval.waiting.message" />
                </TextStyle>
                {approval?.sentOn && (
                  <div>
                    <TextStyle variation="subdued">
                      <DateTimeFormatter
                        type="dateTime"
                        date={approval?.sentOn}
                      />
                    </TextStyle>
                  </div>
                )}
              </div>
            </div>

            {approval?.additionalMessage && (
              <div className="mt10">{approval?.additionalMessage}</div>
            )}
          </div>
        </Card>
        <div className="mt15">
          <TextField
            value={comments}
            multiline
            name="comments"
            onChange={(val) => {
              setComments(val);
            }}
            placeholder={getTranslatedText(intl, 'common.enter.remark.here')}
            type="text"
            label={getTranslatedText(intl, 'common.remark.optional')}
          />
        </div>
        <div>
          <div className="flex jspbtwn mt10">
            <Button onClick={askMoreInfo} disabled={updateLoading}>
              <FormattedMessage id="ticket.details.runbook.needMoreInfo" />
            </Button>

            <ButtonGroup>
              <div>
                <Tooltip
                  title={getTranslatedText(intl, 'common.reject')}
                  position="top-start"
                  inertia
                  className="full-width"
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                  arrow
                >
                  <Button
                    onClick={rejectTicket}
                    disabled={updateLoading}
                    icon={
                      <SVGIcon
                        name="RejectedCircle"
                        color="#B63535 !important"
                      />
                    }
                  ></Button>
                </Tooltip>
              </div>

              <div>
                <Tooltip
                  title={getTranslatedText(intl, 'common.approve')}
                  position="top-start"
                  inertia
                  className="full-width"
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                  arrow
                >
                  <Button
                    onClick={approveTicket}
                    primary
                    disabled={updateLoading}
                    success
                    icon={<SVGIcon name="Check" color="#ffffff" />}
                  ></Button>
                </Tooltip>
              </div>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </Card>
  );
};

function TicketUserApproval(props) {
  const { ticketId } = props;
  const currentUser = getCurrentUserHook();
  const [approvals, setApprovals] = useState(null);

  const { loading, error, data, refetch } = useQuery(GET_APPROVALS, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'ticket.ticketId',
              operator: 'is',
              value: ticketId
            },
            {
              attribute: 'approver.userId',
              operator: 'is',
              value: currentUser?.userId
            },
            {
              attribute: 'status',
              operator: 'is',
              value: 'Sent'
            }
          ]
        }
      }
    },
    onCompleted: (res) => {
      if (res?.getApprovals) {
        setApprovals(res?.getApprovals?.approvals);
      }
    }
  });

  const {
    data: approvalStatuses,
    loading: statusLoading,
    error: statusError
  } = useCachedQuery(GET_ALL_APPROVAL_STATUS);

  const refreshApprovals = () => {
    refetch().then((res) => {
      if (res?.data?.getApprovals?.approvals) {
        setApprovals(res?.data?.getApprovals?.approvals);
      }
    });
  };

  useEffect(() => {
    EventEmitter.subscribe('refetchTicketApproval', refreshApprovals);
    return function cleanup() {
      EventEmitter.unsubscribe('refetchTicketApproval');
    };
  }, []);

  return (
    <>
      {!isEmpty(approvals) && (
        <div className="flex col g10">
          {approvals.map((approval) => (
            <div id="ticket-approval-card">
              <ApprovalCard
                key={approval.id}
                approval={approval}
                approvalStatuses={approvalStatuses}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default withErrorBoundary(TicketUserApproval);
